// effects.ts
import { Injectable, Type } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, tap, take, withLatestFrom, switchMap, filter } from 'rxjs/operators';
import { TemplateService } from '../../services/template/template.service';
import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import * as TemplateActions from './template.actions';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { CustomComponent } from '../../models/CustomComponent.model';
import { CustomSlug } from '../../models/CustomSlug.model';
import { Store } from '@ngrx/store';
import { AppState } from './template.reducers';
import { StatePersistenceUtil } from '../../utils/state-persistence.util';
import { FallbackTemplateService } from '../../services/fallback-template/fallback-template.service';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';

@Injectable()
export class TemplateEffects {
  loadPageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.loadPageDataBySlug),
      withLatestFrom(
        this.store.select(state => state.template.slugs),
        this.store.select(state => state.template.isHydrated)
      ),
      switchMap(([action, slugs, isHydrated]) => {
        const slugData = slugs[action.slug];
        
        // If we have the data and it's loaded, return unchanged
        if (slugData?.loaded) {
          return of(TemplateActions.loadPageDataUnchanged({ slug: action.slug }));
        }

        // Check if we have cached data in localStorage (even if not in NgRx store)
        if (isHydrated && !slugData) {
          const cachedSlug = StatePersistenceUtil.getCachedSlug(action.slug);
          if (cachedSlug) {
            return of(TemplateActions.lazyLoadPageData({
              slug: action.slug,
              staticTemplate: cachedSlug.staticTemplate,
              dynamicTemplate: cachedSlug.dynamicTemplate,
              customSlug: cachedSlug.customSlug,
              data: null
            }));
          }
        }

        // If we have lazy loaded data, process it and mark as properly loaded
        if (slugData?.lazyLoaded && slugData?.lazyLoadedData) {
          const { customSlug, customTemplateArr } = slugData.lazyLoadedData;
          let staticTemplate, dynamicTemplate;
          
          if (Array.isArray(customTemplateArr)) {
            staticTemplate = customTemplateArr.find(t => Number(t.isstatic) === 1);
            dynamicTemplate = customTemplateArr.find(t => Number(t.isstatic) === 0);
          }

          const actions = [];
          if (staticTemplate) {
            actions.push(TemplateActions.loadStaticTemplateSuccess({ slug: action.slug, staticTemplate }));
          }
          if (dynamicTemplate) {
            actions.push(TemplateActions.loadDynamicTemplateSuccess({ slug: action.slug, dynamicTemplate }));
          }
          if (customSlug) {
            actions.push(TemplateActions.loadCustomSlugSuccess({ slug: action.slug, customSlug }));
          }
          actions.push(TemplateActions.loadPageDataSuccess({ 
            slug: action.slug, 
            templatesChanged: true, 
            hasCustomSlug: !!customSlug, 
            hasTemplates: !!(staticTemplate || dynamicTemplate),
            data: slugData.lazyLoadedData
          }));
          return actions;
        }

        // No data in store or cache, fetch from API with enhanced error handling
        return this.templateService.getPageDataBySlug(action.slug, true).pipe(
          map(response => {
            if (!response.data || !response.data.customSlug) {
              return TemplateActions.loadPageDataSuccess({ 
                slug: action.slug, 
                templatesChanged: false,
                hasCustomSlug: false,
                hasTemplates: false
              });
            }
            return { slug: action.slug, data: response.data };
          }),
          mergeMap(result => {
            if ('type' in result) {
              return of(result);
            }
            return this.processPageData(result.slug, result.data);
          }),
          catchError(error => {
            console.error('[TemplateEffects] API call failed for slug:', action.slug, error);
            return this.handleApiError(action.slug, error);
          })
        );
      })
    )
  );

  private handleApiError(slug: string, error: any) {
    const isOffline = !this.connectivityService.isConnected();
    
    // Try to fallback to cached data if API fails
    const cachedSlug = StatePersistenceUtil.getCachedSlug(slug);
    if (cachedSlug) {
      return of(TemplateActions.lazyLoadPageData({
        slug: slug,
        staticTemplate: cachedSlug.staticTemplate,
        dynamicTemplate: cachedSlug.dynamicTemplate,
        customSlug: cachedSlug.customSlug,
        data: null
      }));
    }

    // Check if we should use fallback templates
    if (this.fallbackTemplateService.shouldUseFallback(error, isOffline)) {
      return this.provideFallbackTemplate(slug, error, isOffline);
    }

    // Handle specific error types
    if (error && 
        ((typeof error === 'object' && error.message === 'Page not found') || 
         (error instanceof Error && error.message === 'Page not found'))) {
      
      const errorObj = typeof error === 'object' && !error.slug ? 
        { ...error, slug: slug } : error;
        
      return of(TemplateActions.loadPageDataFailure({ error: errorObj }));
    }

    // For other errors, provide error fallback or mark as loaded with no data
    if (isOffline || this.isNetworkError(error)) {
      return this.provideFallbackTemplate(slug, error, isOffline);
    }

    return of(TemplateActions.loadPageDataSuccess({ 
      slug: slug, 
      templatesChanged: false,
      hasCustomSlug: false,
      hasTemplates: false
    }));
  }

  private provideFallbackTemplate(slug: string, error: any, isOffline: boolean) {
    try {
      const errorType = this.determineErrorType(error, isOffline);
      const fallbackConfig = this.fallbackTemplateService.getFallbackConfig(slug, errorType);
      const fallbackData = this.fallbackTemplateService.getFallbackPageData(fallbackConfig);

      return of(TemplateActions.lazyLoadPageData({
        slug: slug,
        staticTemplate: fallbackData.customTemplateArr[0], // Desktop template
        dynamicTemplate: null,
        customSlug: fallbackData.customSlug,
        data: fallbackData
      }));
    } catch (fallbackError) {
      console.error('[TemplateEffects] Error generating fallback template:', fallbackError);
      
      // Last resort: simple error state
      return of(TemplateActions.loadPageDataSuccess({ 
        slug: slug, 
        templatesChanged: false,
        hasCustomSlug: false,
        hasTemplates: false
      }));
    }
  }

  private determineErrorType(error: any, isOffline: boolean): 'loading' | 'error' | 'offline' | 'notfound' {
    if (isOffline) {
      return 'offline';
    }

    if (error && error.message) {
      const message = error.message.toLowerCase();
      
      if (message.includes('not found') || message.includes('404')) {
        return 'notfound';
      }
      
      if (message.includes('timeout') || message.includes('network') || message.includes('connection')) {
        return 'error';
      }
    }

    return 'error';
  }

  private isNetworkError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message || '';
    return errorMessage.includes('network') || 
           errorMessage.includes('timeout') ||
           errorMessage.includes('connection') ||
           errorMessage.includes('fetch');
  }

  lazyLoadPageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.lazyLoadPageData),
      mergeMap(({ slug, staticTemplate, dynamicTemplate, customSlug, data }) => {
        const actions = [];
        if (staticTemplate) {
          actions.push(TemplateActions.loadStaticTemplateSuccess({ slug, staticTemplate }));
        }
        if (dynamicTemplate) {
          actions.push(TemplateActions.loadDynamicTemplateSuccess({ slug, dynamicTemplate }));
        }
        if (customSlug) {
          actions.push(TemplateActions.loadCustomSlugSuccess({ slug, customSlug }));
        }
        actions.push(TemplateActions.loadPageDataSuccess({ 
          slug, 
          templatesChanged: true, 
          hasCustomSlug: !!customSlug, 
          hasTemplates: !!(staticTemplate || dynamicTemplate),
          data // Include the full data
        }));
        return actions;
      })
    )
  );

  private processPageData(slug: string, data: any) {
    if (!data || typeof data !== 'object') {
      console.error('[TemplateEffects] Invalid data received:', data);
      return [TemplateActions.loadPageDataFailure({ error: 'Invalid data received' })];
    }

    const { customSlug, customTemplateArr } = data;
    const actions: any[] = [];

    // First, process the custom slug if it exists
    if (customSlug) {
      actions.push(TemplateActions.loadCustomSlugSuccess({ slug, customSlug }));
    } else {
      console.warn('[TemplateEffects] No custom slug found for:', slug);
    }

    // Then process templates if they exist
    if (Array.isArray(customTemplateArr)) {
      // Try both boolean and numeric comparison
      const staticTemplate = customTemplateArr.find(t => t.isstatic === 1 || t.isstatic === true);
      const dynamicTemplate = customTemplateArr.find(t => t.isstatic === 0 || t.isstatic === false);

      if (staticTemplate) {
        actions.push(TemplateActions.loadStaticTemplateSuccess({ slug, staticTemplate }));
      }
      
      if (dynamicTemplate) {
        actions.push(TemplateActions.loadDynamicTemplateSuccess({ slug, dynamicTemplate }));
      }
    } else {
      console.warn('[TemplateEffects] No custom templates found for:', slug);
    }

    actions.push(TemplateActions.loadPageDataSuccess({ 
      slug, 
      templatesChanged: true, 
      hasCustomSlug: !!customSlug, 
      hasTemplates: !!(customTemplateArr && customTemplateArr.length > 0),
      data // Include the full data for reference
    }));

    return actions;
  }

  // Helper function to process a template
  private processTemplate(template: any): CustomTemplate {
    if (!template) return {} as CustomTemplate;
    return Object.assign({}, template);
  }

  // Effect to handle 404 errors from page data failures
  handlePageNotFound$ = createEffect(() => 
    this.actions$.pipe(
      ofType(TemplateActions.loadPageDataFailure),
      tap(action => {
      }),
      filter(action => {
        const error = action.error;
        const isPageNotFound = error && 
          ((typeof error === 'object' && error.message === 'Page not found') ||
           (error instanceof Error && error.message === 'Page not found'));
        return !!isPageNotFound;
      }),
      map(() => TemplateActions.navigateTo404())
    )
  );

  constructor(
    private actions$: Actions,
    private templateService: TemplateService,
    private router: Router,
    private componentRegistry: ComponentRegistryService,
    private store: Store<AppState>,
    private fallbackTemplateService: FallbackTemplateService,
    private connectivityService: ConnectivityService
  ) {}
}

@Injectable()
export class RouterEffects {
  navigateTo404$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.navigateTo404),
      tap(() => {
        this.router.navigate(['/404'], { replaceUrl: true });
      })
    ), { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private router: Router
  ) {}
}
